/**
 * Mock data for engagement metrics
 * Generates sample class engagement data based on engagement metric configuration
 */


import { ChartData } from './apiHooks';

// interface ClassEngagementData {
//   classId;
//   className;
//   engagementPercentage;
// }

/**
 * Generate mock class engagement data
 * @param config Engagement metric configuration
 * @returns Chart data formatted for bar chart display
 */
export function generateMockClassEngagementData(
  _config: EngagementMetricConfig
) {
  // Generate a large school with many classes (50 classes)
  const classes = [];
  
  // Generate classes across multiple year levels
  const yearLevels = [7, 8, 9, 10, 11, 12];
  const classLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
  
  let classId = 1;
  for (const year of yearLevels) {
    for (const letter of classLetters) {
      classes.push({
        id: classId++,
        name: `Year ${year}${letter}`,
      });
      
      // Stop at 50 classes
      if (classes.length >= 50) break;
    }
    if (classes.length >= 50) break;
  }

  // Generate engagement percentages (mock data)
  // In real implementation, this would be calculated based on the config
  // Vary the engagement to make it more realistic
  const engagementData: ClassEngagementData[] = classes.map((cls) => {
    // Create some variation - some classes perform better/worse
    const baseEngagement = Math.random() * 40 + 50; // Random between 50-90%
    // Add some year-level variation (older years might have slightly different engagement)
    const yearVariation = (cls.id % 10) * 0.5; // Small variation based on class ID
    const engagementPercentage = Math.max(40, Math.min(95, baseEngagement + yearVariation));
    
    return {
      classId: cls.id,
      className: cls.name,
      engagementPercentage: Math.round(engagementPercentage * 10) / 10,
    };
  });

  // Sort by engagement percentage (descending)
  engagementData.sort((a, b) => b.engagementPercentage - a.engagementPercentage);

  // Format as ChartData
  const labels = engagementData.map((d) => d.className);
  const data = engagementData.map((d) => d.engagementPercentage);

  return {
    labels,
    datasets: [
      {
        label: 'Engagement %',
        data,
        backgroundColor: 'rgba(76, 175, 80, 0.6)',
        borderColor: 'rgba(76, 175, 80, 1)',
        borderWidth: 2,
        tension: 0, // Not used for bar charts, but required by ChartData type
      },
    ],
  };
}

/**
 * Get simplified metric description for the "currentMetricDescription" variable
 * Returns a concise description like "at least 3 days of check-ins per week"
 */
export function getCurrentMetricDescription(config: EngagementMetricConfig) {
  const windowSingular = getEducatorWindowDescriptionSingular(config.window);
  
  // Handle distinct_days metric
  if (config.metric.type === 'distinct_days') {
    const days = config.metric.threshold || 1;
    const eventsPerDay = config.metric.minEventsPerDay || 1;
    
    if (config.window.unit === 'week' && config.window.size === 1) {
      if (eventsPerDay === 1) {
        return `at least ${days} day${days !== 1 ? 's' : ''} of check-ins per week`;
      } else {
        return `at least ${eventsPerDay} check-in${eventsPerDay !== 1 ? 's' : ''} per day on at least ${days} day${days !== 1 ? 's' : ''} per week`;
      }
    } else {
      if (eventsPerDay === 1) {
        return `at least ${days} day${days !== 1 ? 's' : ''} of check-ins during each ${windowSingular}`;
      } else {
        return `at least ${eventsPerDay} check-in${eventsPerDay !== 1 ? 's' : ''} per day on at least ${days} day${days !== 1 ? 's' : ''} during each ${windowSingular}`;
      }
    }
  }
  
  // Handle count metric
  if (config.metric.type === 'count') {
    const count = config.metric.threshold || 1;
    if (config.window.unit === 'week' && config.window.size === 1) {
      return `at least ${count} check-in${count !== 1 ? 's' : ''} per week`;
    } else {
      return `at least ${count} check-in${count !== 1 ? 's' : ''} during each ${windowSingular}`;
    }
  }
  
  // Handle engaged_week metric
  if (config.metric.type === 'engaged_week') {
    const days = config.metric.threshold || 1;
    if (config.window.unit === 'week' && config.window.size === 1) {
      return `at least ${days} day${days !== 1 ? 's' : ''} of check-ins per week`;
    } else {
      return `at least ${days} day${days !== 1 ? 's' : ''} of check-ins during each ${windowSingular}`;
    }
  }
  
  // Fallback to a more general description
  const metricDesc = getEducatorMetricDescription(config.metric);
  return `${metricDesc} during each ${windowSingular}`;
}

/**
 * Get window description for examples (e.g., "week or fortnight")
 */
export function getWindowDescriptionForExample(window: EngagementMetricConfig['window']) {
  if (window.unit === 'period') {
    return 'the entire period';
  }
  
  if (window.unit === 'week') {
    if (window.size === 1) {
      return 'week';
    } else if (window.size === 2) {
      return 'fortnight';
    } else {
      return `${window.size}-week period`;
    }
  } else if (window.unit === 'fortnight') {
    return 'fortnight';
  } else if (window.unit === 'month') {
    if (window.size === 1) {
      return 'month';
    } else {
      return `${window.size}-month period`;
    }
  } else if (window.unit === 'day') {
    if (window.size === 1) {
      return 'day';
    } else {
      return `${window.size}-day period`;
    }
  }
  
  return 'time period';
}

/**
 * Get engagement metric description for display (educator-friendly version)
 */
export function getEngagementMetricDescription(config: EngagementMetricConfig) {
  const periodDesc = getEducatorPeriodDescription(config.period);
  const windowDesc = getEducatorWindowDescription(config.window);
  const windowSingular = getEducatorWindowDescriptionSingular(config.window);
  const metricDesc = getEducatorMetricDescription(config.metric);
  const metricExplanation = getEducatorMetricExplanation(config.metric, config.window);

  // Build a comprehensive, educator-friendly explanation
  const includeEmptyText = config.aggregation.includeEmptyWindows 
    ? ' (including time periods with no check-ins)' 
    : ' (excluding time periods with no check-ins)';
  
  if (config.aggregation.type === 'fraction_of_windows_meeting_threshold') {
    return `For ${periodDesc}, engagement is calculated by dividing the time period into ${windowDesc}. ` +
           `Each ${windowSingular} is considered "engaged" when students ${metricDesc}. ` +
           `The percentage shown represents how many of these ${windowDesc} met this engagement criteria${includeEmptyText}. ` +
           `This metric helps identify the consistency of student engagement throughout ${periodDesc === 'the selected period' ? 'the period' : periodDesc}, ` +
           `showing whether students maintain regular participation patterns or have periods of inconsistent engagement. ` +
           `Higher percentages indicate more consistent engagement, while lower percentages may suggest students need additional support to maintain regular check-in habits.`;
  } else if (config.aggregation.type === 'average_metric_value') {
    const windowContext = windowDesc === 'the entire period' ? 'the period' : windowDesc;
    return `For ${periodDesc}, this metric calculates the average number of check-ins per ${windowSingular} across all ${windowContext}${includeEmptyText}. ` +
           `The calculation divides the total number of check-ins by the number of ${windowContext} in ${periodDesc === 'the selected period' ? 'the period' : periodDesc}. ` +
           `This provides insight into the typical level of student participation and helps track engagement patterns over time. ` +
           `By averaging across ${windowContext}, this metric smooths out day-to-day variations and gives a clearer picture of overall engagement trends. ` +
           `Educators can use this to identify classes or students who may need additional encouragement or support to increase their participation.`;
  } else if (config.aggregation.type === 'sum_metric_value') {
    return `For ${periodDesc}, this metric shows the total number of check-ins across all ${windowDesc}${includeEmptyText}. ` +
           `This cumulative measure helps understand the overall volume of student engagement throughout ${periodDesc === 'the selected period' ? 'the period' : periodDesc}. ` +
           `It's useful for comparing engagement levels between different time periods or classes, as it captures the absolute level of student interaction. ` +
           `Higher totals indicate greater overall participation, which can be valuable for understanding the overall health of student engagement in your program. ` +
           `This metric is particularly helpful when comparing engagement across different terms or when tracking year-over-year changes in student participation.`;
  } else {
    return `For ${periodDesc}, engagement is calculated based on ${windowDesc} where students ${metricDesc}${includeEmptyText}. ` +
           `${metricExplanation} ` +
           `This metric provides insights into student participation and engagement patterns, helping educators understand how actively students are using the check-in system. ` +
           `By tracking these patterns over time, you can identify trends, celebrate improvements, and provide targeted support where needed.`;
  }
}

/**
 * Get educator-friendly period description
 */
function getEducatorPeriodDescription(period: EngagementMetricConfig['period']) {
  switch (period.type) {
    case 'year':
      return period.year ? `${period.year}` : 'the selected year';
    case 'term':
      return period.termId ? `Term ${period.termId}` : 'the selected term';
    case 'month':
      return 'the selected month';
    case 'week':
      return 'the selected week';
    case 'custom':
      if (period.start && period.end) {
        const startDate = new Date(period.start);
        const endDate = new Date(period.end);
        const startFormatted = startDate.toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' });
        const endFormatted = endDate.toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' });
        return `${startFormatted} to ${endFormatted}`;
      }
      return 'the selected period';
    default:
      return 'the selected period';
  }
}

/**
 * Get educator-friendly window description (plural)
 */
function getEducatorWindowDescription(window: EngagementMetricConfig['window']) {
  if (window.unit === 'period') {
    return 'the entire period';
  }
  
  if (window.unit === 'week') {
    if (window.size === 1) {
      return 'weeks';
    } else {
      return `${window.size}-week periods`;
    }
  } else if (window.unit === 'fortnight') {
    if (window.size === 1) {
      return 'fortnights';
    } else {
      return `${window.size * 2}-week periods`;
    }
  } else if (window.unit === 'month') {
    if (window.size === 1) {
      return 'months';
    } else {
      return `${window.size}-month periods`;
    }
  } else if (window.unit === 'day') {
    if (window.size === 1) {
      return 'days';
    } else {
      return `${window.size}-day periods`;
    }
  }
  
  return 'time periods';
}

/**
 * Get educator-friendly window description (singular)
 */
function getEducatorWindowDescriptionSingular(window: EngagementMetricConfig['window']) {
  if (window.unit === 'period') {
    return 'the entire period';
  }
  
  if (window.unit === 'week') {
    if (window.size === 1) {
      return 'week';
    } else {
      return `${window.size}-week period`;
    }
  } else if (window.unit === 'fortnight') {
    if (window.size === 1) {
      return 'fortnight';
    } else {
      return `${window.size * 2}-week period`;
    }
  } else if (window.unit === 'month') {
    if (window.size === 1) {
      return 'month';
    } else {
      return `${window.size}-month period`;
    }
  } else if (window.unit === 'day') {
    if (window.size === 1) {
      return 'day';
    } else {
      return `${window.size}-day period`;
    }
  }
  
  return 'time period';
}

/**
 * Get educator-friendly metric description
 */
function getEducatorMetricDescription(metric: EngagementMetricConfig['metric']) {
  switch (metric.type) {
    case 'count':
      // "utilised the check-in at least X times"
      return `utilised the check-in at least ${metric.threshold} time${metric.threshold !== 1 ? 's' : ''}`;
    case 'distinct_days': {
      // "checked in at least X times per week on at least Y different days"
      const eventsPerDay = metric.minEventsPerDay || 1;
      if (eventsPerDay === 1) {
        if (metric.threshold === 1) {
          return 'checked in at least once';
        } else {
          return `checked in on at least ${metric.threshold} different days`;
        }
      } else {
        if (metric.threshold === 1) {
          return `checked in at least ${eventsPerDay} time${eventsPerDay !== 1 ? 's' : ''} per day`;
        } else {
          return `checked in at least ${eventsPerDay} time${eventsPerDay !== 1 ? 's' : ''} per day on at least ${metric.threshold} different days`;
        }
      }
      break;
    }
    case 'engaged_week':
      // "checked in on at least X different days"
      if (metric.threshold === 1) {
        return 'checked in at least once';
      } else {
        return `checked in on at least ${metric.threshold} different days`;
      }
    default:
      return 'met the engagement criteria';
  }
}

/**
 * Get educator-friendly metric explanation (additional context)
 */
function getEducatorMetricExplanation(
  metric: EngagementMetricConfig['metric'],
  window: EngagementMetricConfig['window']
) {
  const windowSingular = getEducatorWindowDescriptionSingular(window);
  
  switch (metric.type) {
    case 'count':
      return `This means we count how many times students used the check-in feature during each ${windowSingular}, and only count ${windowSingular}s where the total reached the threshold.`;
    case 'distinct_days': {
      const eventsPerDay = metric.minEventsPerDay || 1;
      if (eventsPerDay === 1) {
        return `This focuses on consistency rather than volume - we're looking for ${windowSingular}s where students checked in on multiple different days, showing regular engagement rather than occasional participation.`;
      } else {
        return `This requires both frequency and consistency - students must check in multiple times per day AND do so across multiple days within each ${windowSingular}, demonstrating sustained engagement.`;
      }
      break;
    }
    case 'engaged_week':
      return `This measures whether students checked in on multiple different days within each ${windowSingular}, which indicates regular participation rather than one-off engagement.`;
    default:
      return `This metric evaluates student engagement based on the specified criteria.`;
  }
}


